import React, { useEffect, useState, useCallback } from "react";

import { DialogLayout, IconWithBackground, Button, Badge } from "../ui";
import {
  FeatherCreditCard,
  FeatherCalendar,
  FeatherActivity,
} from "@subframe/core";
import {
  Transaction,
  CustomerTransactionViewDetailsResponse,
  TransactionActivityLog,
} from "../services/api";
import {
  fetchTransactionActivityLogs,
  fetchTransactionViewDetails,
  uncategorizeTransaction,
} from "../services/api";
import {
  useToast,
  ErrorBanner,
  ClosedMonthBanner,
  ClosedMonthConfirmModal,
  ApArWorkbenchUpdateConfirmModal,
} from "../shared/presentation/components";
import {
  useBankTransactionPermissions,
  useIsMonthClosed,
  useIsAdminOrCFO,
} from "../shared/presentation/hooks";
import { useBankTransactionReviewData } from "../shared/presentation/hooks/useBankTransactionReviewData";
import { MODULE_CODES, ACTION_CODES } from "../shared/constants/permissions";
import { ReviewRequestModal } from "../shared/presentation/components/ReviewRequestModal";
import {
  ChangeDetails,
  ACTION_TYPE_MONTH_CLOSE,
} from "../shared/utils/reviewWorkflow";
import { dateToMonthYear } from "../shared/utils/date";
import CustomerPaymentViewContent from "./transactionView/CustomerPaymentViewContent";
import CustomerTransferFromAccountViewContent from "./transactionView/CustomerTransferFromAccountViewContent";
import CustomerInterestIncomeViewContent from "./transactionView/CustomerInterestIncomeViewContent";
import CustomerOtherIncomeViewContent from "./transactionView/CustomerOtherIncomeViewContent";
import CustomerDepositFromOtherAccountsViewContent from "./transactionView/CustomerDepositFromOtherAccountsViewContent";
import CustomerDepositFromOtherAccountsSplitViewContent from "./transactionView/CustomerDepositFromOtherAccountsSplitViewContent";
import SplitCustomerViewContent from "./transactionView/SplitCustomerViewContent";
import CustomerOwnersContributionViewContent from "./transactionView/CustomerOwnersContributionViewContent";
import CustomerVendorPaymentRefundViewContent from "./transactionView/CustomerVendorPaymentRefundViewContent";
import CustomerCreditsOrRefundReceivedViewContent from "./transactionView/CustomerCreditsOrRefundReceivedViewContent";
import CustomerExpenseRefundViewContent from "./transactionView/CustomerExpenseRefundViewContent";
import VendorTransferToAccountViewContent from "./transactionView/VendorTransferToAccountViewContent";

interface CustomerTransactionViewModalProps {
  open: boolean;
  transactionData: Transaction | null;
  onOpenChange: (open: boolean) => void;
  onTransactionUnmatched?: (transactionId: string, amount: number) => void;
}

const CustomerTransactionViewModal: React.FC<
  CustomerTransactionViewModalProps
> = ({ open, transactionData, onOpenChange, onTransactionUnmatched }) => {
  const { showError, showWarning } = useToast();
  const [viewDetails, setViewDetails] = useState<
    CustomerTransactionViewDetailsResponse["data"] | null
  >(null);
  const [activityLogs, setActivityLogs] = useState<TransactionActivityLog[]>(
    [],
  );
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [isUnmatching, setIsUnmatching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Permissions
  const { canUnmatch, needsReviewForUnmatch, isInitialLoading } =
    useBankTransactionPermissions();
  const { reviewStatus, isLoading: isReviewDataLoading } =
    useBankTransactionReviewData(transactionData);

  // Closed month state
  const [showClosedMonthConfirmModal, setShowClosedMonthConfirmModal] =
    useState(false);

  // AP/AR workbench update confirm (customer payment unmatch)
  const [showApArWorkbenchModal, setShowApArWorkbenchModal] = useState(false);

  // Review modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingUnmatchAction, setPendingUnmatchAction] = useState<{
    action: () => Promise<void>;
    changeDetails: ChangeDetails;
    actionCode: string;
  } | null>(null);

  // Closed month check (transaction date)
  const transactionMonth = transactionData?.date
    ? dateToMonthYear(transactionData.date)
    : undefined;
  const { isClosed: isMonthClosed } = useIsMonthClosed(transactionMonth);
  const isAdminOrCFO = useIsAdminOrCFO();

  const transactionId = transactionData?.transaction_id || "";

  const resetState = useCallback(() => {
    setViewDetails(null);
    setActivityLogs([]);
    setErrorMessage(null);
    setIsLoadingDetails(false);
    setIsLoadingLogs(false);
    setIsUnmatching(false);
  }, []);

  useEffect(() => {
    if (open && transactionId) {
      loadTransactionDetails(transactionId);
      loadActivityLogs(transactionId);
    } else {
      resetState();
    }
  }, [open, transactionId, resetState]);

  const loadTransactionDetails = async (id: string) => {
    try {
      setIsLoadingDetails(true);
      setErrorMessage(null);
      const response = await fetchTransactionViewDetails(id);
      setViewDetails(response.data);
    } catch (error: any) {
      console.error("Failed to fetch transaction view details:", error);
      setErrorMessage(error?.message || "Failed to load transaction details.");
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const loadActivityLogs = async (id: string) => {
    try {
      setIsLoadingLogs(true);
      const response = await fetchTransactionActivityLogs(id);
      const logs = Array.isArray(response.data?.logs) ? response.data.logs : [];
      setActivityLogs(logs);
    } catch (error) {
      console.error("Failed to fetch transaction activity logs:", error);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const resolveTransferIds = () => {
    const details = viewDetails?.category_details as
      | { to_transaction_id?: string; from_transaction_id?: string }
      | undefined;

    if (
      !details ||
      !(
        viewDetails?.category_type === "transfer_from_another_account" ||
        viewDetails?.category_type === "transfer_to_another_account"
      )
    ) {
      return { fromId: undefined, toId: undefined };
    }

    const { to_transaction_id: toId, from_transaction_id: fromId } = details;

    return { fromId, toId };
  };

  const proceedWithUnmatchFlow = async () => {
    if (!transactionId) return;

    // Closed month: non-Admin/CFO must submit via review
    if (isMonthClosed === true && !isAdminOrCFO) {
      setShowClosedMonthConfirmModal(true);
      return;
    }

    if (isReviewDataLoading) {
      return;
    }

    // FBAC: open review modal when no review yet, or after rejection (resubmit)
    const needsReviewSubmission = needsReviewForUnmatch;

    if (needsReviewForUnmatch && reviewStatus === "pending") {
      showWarning(
        "This transaction has a pending review request. Please wait for manager approval.",
      );
      return;
    }

    if (needsReviewSubmission) {
      const changeDetails: ChangeDetails = {
        old_value: transactionData?.category_type || "categorized",
        new_value: "uncategorized",
        reason: "Unmatch transaction requires manager approval",
        raw_json: {
          transaction_id: transactionId,
          description: transactionData?.description,
          amount: transactionData?.amount,
          category_type:
            viewDetails?.category_type ?? transactionData?.category_type,
          category_details: viewDetails?.category_details || null,
          currency_symbol: transactionData?.currency_symbol,
          date: transactionData?.date,
        },
      };

      setPendingUnmatchAction({
        action: async () => {
          await proceedWithUnmatchFlow();
        },
        changeDetails,
        actionCode: ACTION_CODES.UNMATCH,
      });
      setIsReviewModalOpen(true);
      return;
    }

    try {
      setIsUnmatching(true);
      const { fromId, toId } = resolveTransferIds();
      await uncategorizeTransaction(transactionId, fromId, toId);
      if (transactionData?.amount) {
        onTransactionUnmatched?.(transactionId, transactionData.amount);
      }
      onOpenChange(false);
    } catch (error: any) {
      console.error("Failed to uncategorize transaction:", error);
      showError(error?.message || "Failed to uncategorize transaction");
    } finally {
      setIsUnmatching(false);
    }
  };

  const handleUnmatch = async () => {
    if (!transactionId) return;

    if (!canUnmatch) {
      showError("You do not have permission to unmatch transactions");
      return;
    }

    // Customer payment unmatch: show AP/AR workbench update notice first
    if (viewDetails?.category_type === "customer_payment") {
      setShowApArWorkbenchModal(true);
      return;
    }

    await proceedWithUnmatchFlow();
  };

  const customerPaymentDetails =
    viewDetails?.category_type === "customer_payment"
      ? viewDetails.category_details
      : null;

  const transferFromAccountDetails =
    viewDetails?.category_type === "transfer_from_another_account"
      ? viewDetails.category_details
      : null;

  const interestIncomeDetails =
    viewDetails?.category_type === "interest_income"
      ? viewDetails.category_details
      : null;

  const otherIncomeDetails =
    viewDetails?.category_type === "other_income"
      ? viewDetails.category_details
      : null;

  const depositFromOtherAccountsDetails =
    viewDetails?.category_type === "deposit"
      ? viewDetails.category_details
      : null;

  const depositSplitDetails =
    viewDetails?.category_type === "deposit_split"
      ? viewDetails.category_details
      : null;

  const splitCustomerDetails =
    viewDetails?.category_type === "split_customer"
      ? viewDetails.category_details
      : null;

  const ownersContributionDetails =
    viewDetails?.category_type === "owners_contribution"
      ? viewDetails.category_details
      : null;

  const vendorPaymentRefundDetails =
    viewDetails?.category_type === "vendor_payment_refund"
      ? viewDetails.category_details
      : null;

  const creditsOrRefundReceivedDetails =
    viewDetails?.category_type === "credits_or_refund_received"
      ? viewDetails.category_details
      : null;

  const invoices = customerPaymentDetails?.invoices || [];

  const categoryTypeForDisplay =
    viewDetails?.category_type || "customer_payment";

  const advancePayment = customerPaymentDetails?.advance_payment || 0;

  const customerName =
    customerPaymentDetails?.customer_name ||
    transactionData?.description ||
    "Customer";

  const renderCategorySection = () => {
    if (!viewDetails) {
      return (
        <div className="flex w-full flex-col items-center justify-center rounded-md border border-dashed border-neutral-border bg-neutral-50 px-6 py-12 text-body text-subtext-color">
          {isLoadingDetails
            ? "Loading transaction details..."
            : "Transaction details are unavailable."}
        </div>
      );
    }

    switch (viewDetails.category_type) {
      case "customer_payment":
        return (
          <CustomerPaymentViewContent
            customerName={customerName}
            categoryType={categoryTypeForDisplay}
            advancePayment={advancePayment}
            invoices={invoices}
            isLoading={isLoadingDetails}
            transactionCurrencySymbol={transactionData?.currency_symbol}
            exchangeRate={customerPaymentDetails?.exchange_rate}
            bankCharges={customerPaymentDetails?.bank_charges}
          />
        );
      case "transfer_to_another_account":
        return (
          <VendorTransferToAccountViewContent
            details={viewDetails.category_details}
            isLoading={isLoadingDetails}
            currencySymbol={transactionData?.currency_symbol || ""}
          />
        );
      case "transfer_from_another_account":
        return (
          <CustomerTransferFromAccountViewContent
            details={transferFromAccountDetails || null}
            isLoading={isLoadingDetails}
          />
        );
      case "interest_income":
        return (
          <CustomerInterestIncomeViewContent
            details={interestIncomeDetails || null}
            isLoading={isLoadingDetails}
          />
        );
      case "customer_expense_refund":
        return (
          <CustomerExpenseRefundViewContent
            details={viewDetails.category_details}
            isLoading={isLoadingDetails}
          />
        );
      case "other_income":
        return (
          <CustomerOtherIncomeViewContent
            details={otherIncomeDetails || null}
            isLoading={isLoadingDetails}
          />
        );
      case "deposit":
        return (
          <CustomerDepositFromOtherAccountsViewContent
            details={depositFromOtherAccountsDetails || null}
            isLoading={isLoadingDetails}
          />
        );
      case "deposit_split":
        return (
          <CustomerDepositFromOtherAccountsSplitViewContent
            details={depositSplitDetails || null}
            isLoading={isLoadingDetails}
          />
        );
      case "split_customer":
        return (
          <SplitCustomerViewContent
            details={splitCustomerDetails || null}
            isLoading={isLoadingDetails}
            transactionCurrencySymbol={transactionData?.currency_symbol}
          />
        );
      case "owners_contribution":
        return (
          <CustomerOwnersContributionViewContent
            details={ownersContributionDetails || null}
            isLoading={isLoadingDetails}
          />
        );
      case "vendor_payment_refund":
        return (
          <CustomerVendorPaymentRefundViewContent
            details={vendorPaymentRefundDetails || null}
            isLoading={isLoadingDetails}
          />
        );
      case "credits_or_refund_received":
        return (
          <CustomerCreditsOrRefundReceivedViewContent
            details={creditsOrRefundReceivedDetails || null}
            isLoading={isLoadingDetails}
          />
        );
      default:
        return (
          <div className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed border-neutral-border bg-neutral-50 px-6 py-12 text-center">
            <span className="text-heading-4 font-heading-4 text-default-font">
              Unsupported Category
            </span>
            <span className="text-body text-subtext-color">
              Viewing details for this transaction type is not available yet.
            </span>
          </div>
        );
    }
  };

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-256 flex-col items-start gap-6 bg-default-background px-8 py-6">
        <div className="flex w-full items-center justify-between">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Transaction Details
          </span>
        </div>

        {errorMessage && (
          <ErrorBanner
            errors={{ general: errorMessage }}
            title="Unable to load transaction details"
          />
        )}

        {/* Review Status Banners */}
        {reviewStatus === "pending" && (
          <div className="flex w-full items-center gap-3 rounded-lg border border-warning-300 bg-warning-50 px-4 py-3">
            <span className="text-body text-warning-700">
              This transaction has a pending review request. Please wait for
              manager approval.
            </span>
          </div>
        )}

        {reviewStatus === "rejected" && (
          <div className="flex w-full items-center gap-3 rounded-lg border border-error-300 bg-error-50 px-4 py-3">
            <span className="text-body text-error-700">
              Your review request was rejected. You can submit a new request
              using Unmatch, or contact your manager.
            </span>
          </div>
        )}

        {isMonthClosed === true && <ClosedMonthBanner />}

        {transactionData && (
          <div className="flex w-full flex-col items-start gap-6 rounded-lg border border-solid border-neutral-border bg-neutral-50 px-8 py-6 shadow-sm">
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-3">
                  <IconWithBackground icon={<FeatherCreditCard />} />
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    {transactionData.description}
                  </span>
                </div>
                <span className="text-heading-3 font-heading-3 text-success-600">
                  {transactionData.currency_symbol || "₹"}
                  {transactionData.amount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              <div className="flex w-full flex-wrap items-center gap-6 border-t border-solid border-neutral-border pt-4">
                <div className="flex items-center gap-2">
                  <FeatherCalendar className="text-body font-body text-neutral-400" />
                  <span className="text-body-bold font-body-bold text-default-font">
                    {new Date(transactionData.date).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <FeatherActivity className="text-body font-body text-neutral-400" />
                  <span className="text-body-bold font-body-bold text-default-font">
                    Deposit
                  </span>
                </div>
                <Badge variant="success">
                  {transactionData.user_status || "Reconciled"}
                </Badge>
              </div>
            </div>
          </div>
        )}

        {renderCategorySection()}

        <div className="flex w-full flex-col gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex items-center justify-between">
            <span className="text-heading-4 font-heading-4 text-default-font">
              Activity Logs
            </span>
          </div>
          {isLoadingLogs ? (
            <div className="flex w-full items-center justify-center py-4 text-body text-subtext-color">
              Loading activity logs...
            </div>
          ) : activityLogs.length === 0 ? (
            <span className="text-body text-subtext-color">
              No activity logs available.
            </span>
          ) : (
            <div className="flex flex-col gap-3">
              {activityLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex flex-col gap-1 rounded border border-neutral-border px-4 py-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-body-bold text-default-font">
                      {log.activity_name}
                    </span>
                    <span className="text-caption text-subtext-color">
                      {new Date(log.created_at).toLocaleString()}
                    </span>
                  </div>
                  <span className="text-caption text-subtext-color">
                    By {log.user_name}
                  </span>
                  <span className="text-body text-default-font">
                    {log.description}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-neutral-border pt-4">
          <Button
            variant="neutral-tertiary"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          <Button
            variant="brand-secondary"
            onClick={handleUnmatch}
            loading={isInitialLoading || isUnmatching || isReviewDataLoading}
            disabled={
              isInitialLoading ||
              isUnmatching ||
              isReviewDataLoading ||
              !transactionId ||
              !canUnmatch ||
              reviewStatus === "pending"
            }
          >
            Unmatch
          </Button>
        </div>

        {/* AP/AR workbench update confirm - before unmatch for customer payment */}
        <ApArWorkbenchUpdateConfirmModal
          open={showApArWorkbenchModal}
          onOpenChange={setShowApArWorkbenchModal}
          entity="customer"
          onConfirm={() => {
            setShowApArWorkbenchModal(false);
            proceedWithUnmatchFlow();
          }}
          loading={isUnmatching}
        />

        {/* Closed month confirm modal - when non-Admin/CFO tries to unmatch in closed month */}
        <ClosedMonthConfirmModal
          open={showClosedMonthConfirmModal}
          onOpenChange={setShowClosedMonthConfirmModal}
          entityLabel="Bank transaction"
          onConfirm={() => {
            setShowClosedMonthConfirmModal(false);
            const changeDetails: ChangeDetails = {
              closed_month: true,
              action_types: ["unmatch", ACTION_TYPE_MONTH_CLOSE],
              old_value: transactionData?.category_type || "categorized",
              new_value: "uncategorized",
              reason: "Unmatch in closed month requires review",
              raw_json: {
                transaction_id: transactionId,
                description: transactionData?.description,
                amount: transactionData?.amount,
                category_type:
                  viewDetails?.category_type ?? transactionData?.category_type,
                category_details: viewDetails?.category_details || null,
                currency_symbol: transactionData?.currency_symbol,
                date: transactionData?.date,
              },
            };
            setPendingUnmatchAction({
              action: async () => {
                await proceedWithUnmatchFlow();
              },
              changeDetails,
              actionCode: ACTION_CODES.UNMATCH,
            });
            setIsReviewModalOpen(true);
          }}
        />

        {/* Review Request Modal */}
        {isReviewModalOpen && pendingUnmatchAction && transactionData && (
          <ReviewRequestModal
            open={isReviewModalOpen}
            onOpenChange={setIsReviewModalOpen}
            entityType="bank_transaction"
            entityId={transactionData.id!}
            moduleCode={MODULE_CODES.BANK_TRANSACTION}
            actionCode={pendingUnmatchAction.actionCode}
            changeDetails={pendingUnmatchAction.changeDetails}
            transactionData={transactionData}
            onSuccess={() => {
              setIsReviewModalOpen(false);
              setPendingUnmatchAction(null);
              onOpenChange(false);
            }}
            skipNavigation={true}
          />
        )}
      </div>
    </DialogLayout>
  );
};

export default CustomerTransactionViewModal;
